import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import {
  CloudCheckIcon,
  CloudIcon,
  CloudWarningIcon,
  CloudArrowDownIcon,
  WindowsLogoIcon,
  PackageIcon,
  GoogleDriveLogoIcon,
} from '@phosphor-icons/react';
import { Popover } from '@tranquility-ai/theme-components/popover';
import '@tranquility-ai/theme-components/popover.css';
import { FetchWithVersionCheck, CaptureError } from '@/shared/functions';
import { recordStat } from '@/shared/functions';
import { StatsContext, FeaturesContext, ToastContext, AuthContext } from '@/app/contexts';
import { Button } from '@tranquility-ai/theme-components/button';
import { RemoteStorage } from '@/shared/types';
import { ToastLevel } from './toast';
import { useSyncStatus, SyncStatusIndicator } from './sync-status';
import { isStatus } from '@/shared/clientFunctions';
import { useAppFormatters } from '@/i18n/useAppFormatters';
import { useTimePilotT } from '@/i18n/useTimePilotT';
import {
  statusBarIconButtonClassName,
  statusBarIconButtonProps,
} from '../status-bar-icon-button';

export interface BacklogFromApi {
  processingQueues: {
    [key: string]: number;
  };
}

export interface SimpleStatusCounts {
  IN_PROGRESS: number;
  COMPLETED: number;
  FAILED: number;
}

export interface BacklogProps {
  backlogData: BacklogFromApi;
  provenanceStatusCounts: { [key: string]: number };
  userId: string;
  caseId: string | undefined;
  remoteStorageLink: RemoteStorage | null;
}

const Backlog = (props: BacklogProps) => {
  const t = useTimePilotT();
  const { formatDate, formatTime } = useAppFormatters();
  const { backlogData, provenanceStatusCounts, userId, caseId, remoteStorageLink } = props;

  const [isOpen, setOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [totalProcessingQueueMessages, setTotalProcessingQueueMessages] = useState(0);
  const [lastSyncedTime, setLastSyncedTime] = useState<string | null>(null);
  const [processingQueueData, setProcessingQueueData] = useState<{ [key: string]: number } | undefined>(undefined);
  const [sharepointLinkLocal, setSharepointLinkLocal] = useState<string | null>(null);
  const [simpleStatusCounts, setSimpleStatusCounts] = useState<SimpleStatusCounts>({
    IN_PROGRESS: 0,
    COMPLETED: 0,
    FAILED: 0,
  });

  const { featureFlags } = useContext(FeaturesContext) || {};
  const statsContext = useContext(StatsContext);
  const { setToast, setToastOpen } = useContext(ToastContext);
  const { idToken, accessToken, tenantName } = useContext(AuthContext);

  const { syncStatus } = useSyncStatus(!!featureFlags?.syncStatus);

  useEffect(() => {
    if (provenanceStatusCounts) {
      const newSimpleStatusCounts: SimpleStatusCounts = {
        IN_PROGRESS: 0,
        COMPLETED: 0,
        FAILED: 0,
      };
      Object.entries(provenanceStatusCounts).forEach(([status, count]) => {
        if (isStatus(status, 'INTERMEDIARY')) {
          newSimpleStatusCounts.IN_PROGRESS += count;
        } else if (isStatus(status, 'SUCCESS') || isStatus(status, 'SKIPPED') || isStatus(status, 'PARTIAL_SUCCESS')) {
          newSimpleStatusCounts.COMPLETED += count;
        } else if (isStatus(status, 'ERROR')) {
          newSimpleStatusCounts.FAILED += count;
        }
      });
      setSimpleStatusCounts(newSimpleStatusCounts);
    }
  }, [provenanceStatusCounts]);

  const handleSyncData = async () => {
    setIsSyncing(true);
    try {
      const result = await FetchWithVersionCheck('/api/protected/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ userId, selectedCase: caseId })
      });
      if (result.ok) {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 15000,
          title: t('backlog.success'),
          description: t('backlog.syncStarted'),
          level: ToastLevel.SUCCESS,
          actionButtonText: t('backlog.contactCsm'),
          actionButtonOnClick: () => {
            window.open('mailto:csm@tranquility-ai.com', '_blank');
          },
        });
      } else {
        const errorMessage = `Failed to sync data: ${result.statusText}`;
        CaptureError(errorMessage, new Error(errorMessage));
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 15000,
          title: t('backlog.error'),
          description: errorMessage,
          level: ToastLevel.ERROR,
        });
      }
    } catch (error) {
      const errorMessage = `Error syncing data for user ${userId}: ${error}`;
      CaptureError(errorMessage, error as Error);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 15000,
        title: t('backlog.error'),
        description: t('backlog.syncFailed'),
        level: ToastLevel.ERROR,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  useEffect(() => {
    if (backlogData?.processingQueues) {
      const processingQueueDataIn = backlogData.processingQueues;
      setProcessingQueueData(processingQueueDataIn);
      const totalProcessingQueueMessagesIn = processingQueueDataIn ? Object.values(processingQueueDataIn).reduce((acc, curr) => acc + curr, 0) : 0;
      setTotalProcessingQueueMessages(totalProcessingQueueMessagesIn);
    }
    if (syncStatus?.length) {
      const latestUnixSeconds = syncStatus.reduce((latest, row) => {
        const rowLastSync = Math.max(row.lastSyncEnd ?? 0, row.lastFileCopiedTime ?? 0);
        return Math.max(latest, rowLastSync);
      }, 0);

      if (latestUnixSeconds > 0) {
        const latestDate = new Date(latestUnixSeconds * 1000);
        const dateOptions = { month: '2-digit' as const, day: '2-digit' as const, year: 'numeric' as const };
        const today = formatDate(new Date(), dateOptions);
        const latestDateStr = formatDate(latestDate, dateOptions);
        const latestTimeStr = formatTime(latestDate, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setLastSyncedTime(latestDateStr === today ? latestTimeStr : `${latestDateStr} ${latestTimeStr}`);
      } else {
        setLastSyncedTime(null);
      }
    } else {
      setLastSyncedTime(null);
    }
  }, [backlogData, formatDate, formatTime, syncStatus]);

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (open) {
      recordStat(
        'BacklogPopoverRead',
        new MouseEvent('hover') as unknown as React.MouseEvent,
        statsContext      );
    }
  };

  const remoteStorageHref = remoteStorageLink?.url?.trim() ?? '';
  const hasRemoteStorageLink = remoteStorageHref !== '';
  const showBacklogDetails = !!featureFlags?.backlog;

  const syncNowButton = (
    <Button
      type="button"
      size="sm"
      style="solid"
      color="accent"
      background="default"
      id="SyncNowButton"
      leadingIcon={<CloudArrowDownIcon weight="bold" />}
      isLoading={isSyncing}
      onClick={(event) => {
        handleSyncData();
        recordStat(
          'SyncNowButtonClicked',
          event,
          statsContext        );
      }}
    >
      {t('backlog.syncNow')}
    </Button>
  );

  return (
    <div className="flex flex-row items-center gap-2">
      <Popover
        id="BacklogPopoverRead"
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        placement={featureFlags?.enableMediaAndSourceShare ? "top-start" : "bottom-end"}
        variant="inverted"
        content={
          showBacklogDetails || hasRemoteStorageLink ? (
          <div className="flex flex-col justify-between gap-2 text-[var(--tp-popover-fg)]">
            {showBacklogDetails && (
              <>
                {(simpleStatusCounts.IN_PROGRESS > 0 || simpleStatusCounts.COMPLETED > 0 || simpleStatusCounts.FAILED > 0) && (
                  <>
                    <div className="flex flex-row items-center justify-between gap-2">
                      <div className="text-[16px] font-semibold">{t('backlog.fileStatus')}</div>
                      <div className="flex flex-row items-center gap-2">
                        <div className="p-1 border border-solid border-black/60 rounded-full flex items-center justify-center gap-1">
                          <CloudCheckIcon size={22} weight="duotone" className="w-6 h-6 text-green-500" />
                          <div className="text-[12px] font-semibold">{simpleStatusCounts.COMPLETED || 0}</div>
                        </div>
                        <div className="p-1 border border-solid border-black/60 rounded-full flex items-center justify-center gap-1">
                          <CloudIcon size={22} weight="duotone" className="w-6 h-6 text-yellow-500" />
                          <div className="text-[12px] font-semibold">{simpleStatusCounts.IN_PROGRESS || 0}</div>
                        </div>
                        <div className="p-1 border border-solid border-black/60 rounded-full flex items-center justify-center gap-1">
                          <CloudWarningIcon size={22} weight="duotone" className="w-6 h-6 text-red-500" />
                          <div className="text-[12px] font-semibold">{simpleStatusCounts.FAILED || 0}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-row items-center justify-start gap-2 bg-gray-950 h-[2px] w-full">
                    </div>
                  </>
                )}
                <div className="flex flex-row items-center justify-left gap-2 w-full">
                  <div className="flex flex-col justify-left gap-2">
                    <div className="text-[14px] font-semibold">{t('backlog.queueStatus')}</div>
                      {processingQueueData && (
                        <div className="flex flex-col justify-left gap-2 rounded-md bg-white/10 pl-1">
                          {Object.entries(backlogData.processingQueues).map(([queueUrl, queue]) => (
                            <div key={queueUrl} className="flex flex-row items-center justify-left gap-2">
                              <div className="text-[12px] font-semibold">
                                {t('backlog.processingQueue')}
                              </div>
                              <div className="text-[12px]">
                                {t(queue === 1 ? 'backlog.fileCountOne' : 'backlog.fileCountMany', { count: queue })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              </>
            )}
            <div
              className={`flex flex-row items-center gap-2 ${
                hasRemoteStorageLink ? 'w-full justify-between' : 'justify-center'
              }`}
            >
              {hasRemoteStorageLink ? (
                  <a
                    className="flex flex-row items-center justify-center gap-2 px-2 py-1 border border-solid border-black/80 hover:border-black/40 hover:cursor-pointer rounded-full opacity-60 hover:opacity-100 bg-white/10 no-underline text-black/80 hover:text-black/100"
                    id="Sharepoint Link"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={remoteStorageHref}
                  >
                    {remoteStorageLink?.type === 'Google Drive' ? (
                      <>
                        <GoogleDriveLogoIcon size={22} />
                        <label className="cursor-pointer">{t('backlog.googleDrive')}</label>
                      </>
                    ) : remoteStorageLink?.type === 'SharePoint' ? (
                      <>
                        <WindowsLogoIcon size={22} />
                        <label className="cursor-pointer">{t('backlog.sharePoint')}</label>
                      </>
                    ) : (
                      <>
                        <PackageIcon size={22} />
                        <label className="cursor-pointer">{remoteStorageLink?.type}</label>
                      </>
                    )}
                  </a>
              ) : null}
              {syncNowButton}
            </div>
          </div>
          ) : (
            syncNowButton
          )
        }
      >
        {/* AGENT: do not restyle this trigger. Spread statusBarIconButtonProps
            and className={statusBarIconButtonClassName} only. Regular (not
            duotone) glyph. Last-synced copy is a sibling, never inside Button. */}
        <Button
          type="button"
          {...statusBarIconButtonProps}
          id="BacklogPopover"
          aria-label={lastSyncedTime ? t('backlog.lastSyncedAt', { time: lastSyncedTime }) : t('backlog.title')}
          className={statusBarIconButtonClassName}
          onMouseEnter={(event) => recordStat(
            'BacklogPopoverClicked',
            event,
            statsContext          )}
          onClick={() => handleOpenChange(!isOpen)}
        >
          {featureFlags?.syncStatus ? (
            <SyncStatusIndicator
              syncStatus={syncStatus}
              iconSize={20}
              fallback={<CloudCheckIcon weight="regular" />}
            />
          ) : (
            <CloudCheckIcon weight="regular" />
          )}
        </Button>
      </Popover>
      {lastSyncedTime ? (
        <span className="whitespace-nowrap text-sm text-white">
          {t('backlog.lastSynced', { time: lastSyncedTime })}
        </span>
      ) : null}
    </div>
  );
};

Backlog.displayName = 'Backlog';

export default Backlog;
